import { useState } from 'react';
import { Mail, Wand2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { buscarHtmlEnviado, useLeadEmailsEnviados } from '@/hooks/useLeadEmailsEnviados';
import type { ItemPrevia, PreviaDoLead } from '@/lib/previaPropostaApi';

/**
 * PRÉVIA DE PROPOSTA POR TOP (Douglas 05/10/2026, desenho aprovado).
 *
 * A proposta pronta do TOP do lead — a mesma que o e-mail do TOP mostra — no
 * lugar onde ficava o e-mail de campanha enviado. Sempre diz o que tem dos
 * dois lados: hoje (o que ele paga e tem) × Claro (o que vai e quanto custa).
 * "Usar esta proposta" preenche o montador; de lá saem imagem, e-mail,
 * WhatsApp e o pedido, como sempre.
 */

const FONTE: Record<string, string> = { PORT: 'Portabilidade Vivo', TT: 'TT', RENOVACAO: 'Renovação', OI: 'Oi', ALGAR: 'Algar' };

const brl = (v: number | null | undefined) =>
  v == null ? '—' : v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const dataHora = (s: string | null) =>
  s ? new Date(s).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '';

/** Linhas iguais viram uma linha só ("2 linhas TT · Claro Pós 12 GB cada"). */
function agrupar(itens: ItemPrevia[]) {
  const grupos: Array<{ chave: string; itens: ItemPrevia[] }> = [];
  for (const i of itens) {
    const chave = `${i.tipo}|${i.codigo ?? i.descricao}|${i.valor}`;
    const g = grupos.find((x) => x.chave === chave);
    if (g) g.itens.push(i); else grupos.push({ chave, itens: [i] });
  }
  return grupos;
}

function rotuloDoGrupo(itens: ItemPrevia[]): string {
  const [i] = itens;
  const n = itens.length;
  if (n === 1) return i.descricao;
  switch (i.tipo) {
    case 'TT': return `${n} linhas TT · mesmo número e chip · Claro Pós ${i.gb} GB cada`;
    case 'LINHA_NOVA': return `${n} linhas novas · Claro Pós ${i.gb} GB cada`;
    case 'PORTABILIDADE': return `${n} linhas ${i.gb} GB cada · mesmo número`;
    case 'RENOVACAO': return `${n} linhas renovadas · Renovação ${i.gb} GB cada${i.descricao.includes('não cai') ? ' (o GB não cai)' : ''}`;
    case 'BLOCO': return `${n} Blocos de Dados de ${i.gb} GB`;
    default: return `${n} × ${i.descricao}`;
  }
}

function valorDoGrupo(itens: ItemPrevia[]): string {
  const [i] = itens;
  if (i.tipo === 'BONUS') return `${i.gb} GB`;
  return `${brl(itens.reduce((s, x) => s + (x.valor || 0), 0))} por mês`;
}

const numerosDoGrupo = (itens: ItemPrevia[]) =>
  itens.map((i) => i.numero).filter((n): n is string => !!n && n.length >= 4);

/** O que a linha tem hoje (renovação e portabilidade): plano, uso e valor. */
function hojeDoGrupo(itens: ItemPrevia[]): string | null {
  const comHoje = itens.filter((i) => i.plano_atual || i.valor_atual != null);
  if (!comHoje.length) return null;
  const um = (i: ItemPrevia) => [
    i.plano_atual ? String(i.plano_atual).toLowerCase().replace(/(^|\s)\S/g, (m) => m.toUpperCase()) : null,
    i.tipo === 'PORTABILIDADE' && i.gb_atual != null ? `usa ${Math.round(Number(i.gb_atual))} GB` : null,
    i.valor_atual != null ? brl(Number(i.valor_atual)) : null,
  ].filter(Boolean).join(' · ');
  const textos = [...new Set(comHoje.map(um))];
  return textos.length === 1 ? `hoje: ${textos[0]}${comHoje.length > 1 ? ' cada' : ''}` : `hoje: ${textos.join(' | ')}`;
}

/** O que vai, sem a fibra e o bônus (no quadro da renovação a fibra fica à parte). */
function resumoSemFibra(itens: ItemPrevia[]): string {
  const linhas = itens.filter((i) => ['TT', 'LINHA_NOVA', 'PORTABILIDADE', 'RENOVACAO'].includes(i.tipo)).length;
  const blocos = itens.filter((i) => i.tipo === 'BLOCO').length;
  const gb = itens.filter((i) => ['TT', 'LINHA_NOVA', 'PORTABILIDADE', 'RENOVACAO', 'BLOCO'].includes(i.tipo)).reduce((s, i) => s + (i.gb || 0), 0);
  return [
    linhas ? `${linhas} ${linhas === 1 ? 'linha' : 'linhas'}` : null,
    blocos ? `${blocos} ${blocos === 1 ? 'Bloco de Dados' : 'Blocos de Dados'}` : null,
    itens.some((i) => i.tipo === 'APARELHO') ? 'aparelho novo' : null,
    gb ? `${gb} GB` : null,
  ].filter(Boolean).join(' · ');
}

export function PreviaPropostaTopCard({ leadId, dados, onUsar }: {
  leadId: string;
  dados: PreviaDoLead;
  onUsar: (dados: PreviaDoLead) => void;
}) {
  const { previa, fonte, top, top_nome } = dados;
  const { data: emails } = useLeadEmailsEnviados(leadId);
  const [html, setHtml] = useState<string | null>(null);
  const ultimo = emails?.[0] ?? null;

  const temLinhaNova = previa.itens.some((i) => i.tipo === 'LINHA_NOVA');
  const bonus = previa.itens.find((i) => i.tipo === 'BONUS');
  // Com linha nova, o bônus vai ao lado dela ("12 GB + 50 GB"); senão, numa linha própria.
  const itensVisiveis = previa.itens.filter((i) => !(i.tipo === 'BONUS' && temLinhaNova));
  const fibra = previa.itens.find((i) => i.tipo === 'FIBRA');
  const renovacao = fonte === 'RENOVACAO' && previa.itens.some((i) => i.tipo === 'RENOVACAO');
  const semFibra = Math.round(((previa.total_3m - (fibra?.valor ?? 0))) * 100) / 100;

  const abrirEmail = async (id: string) => {
    setHtml((await buscarHtmlEnviado(id)) || "<p style='font-family:Arial;padding:16px'>Este e-mail saiu antes de o CRM guardar a cópia.</p>");
  };

  return (
    <section className="overflow-hidden rounded-xl border border-border border-l-[3px] border-l-[#5B0A0D] bg-card pl-0 shadow-[0_1px_2px_rgba(60,20,30,0.05),0_4px_14px_-6px_rgba(60,20,30,0.08)] dark:border-l-[#C87484]">
      <header className="flex min-h-[30px] flex-wrap items-center gap-2 border-b border-border bg-[#FAF8F4] px-3 py-1.5 dark:bg-[#241A1A]">
        <span className="text-[10px] font-bold uppercase tracking-[1.2px] text-[#5B0A0D] dark:text-[#C87484]">
          Prévia de proposta · TOP {top} · {FONTE[fonte] ?? fonte}
        </span>
        {top_nome && <span className="rounded-full border border-border bg-card px-2 text-[10.5px] text-muted-foreground">{top_nome}</span>}
        <span className="ml-auto text-[10.5px] text-muted-foreground">{fonte === 'PORT' ? 'a mesma do e-mail do TOP' : 'é a que o e-mail do TOP vai mostrar'}</span>
      </header>

      {previa.pedir_conta ? (
        <div className="space-y-2 px-3 py-3">
          <p className="text-[12px] text-foreground">
            Sem dado para montar a proposta deste cliente ({previa.hoje.resumo}). O e-mail do TOP pede a conta e já mostra os nossos valores:
          </p>
          <div className="overflow-hidden rounded-lg border border-border">
            {(previa.nossos_valores ?? []).map((n) => (
              <div key={n.descricao} className="flex items-center justify-between border-b border-border px-3 py-1.5 text-[12px] last:border-b-0">
                <span>{n.descricao}</span>
                <span className="font-semibold tabular-nums text-[#5B0A0D] dark:text-[#C87484]">{brl(n.valor)} por mês</span>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-2 px-3 pt-3">
            <div className="rounded-lg bg-[#F2EEE8] px-3 py-2 dark:bg-[#241A1A]">
              <p className="text-[10.5px] text-muted-foreground">Hoje</p>
              <p className="font-display text-[19px] font-semibold tabular-nums text-foreground">
                {previa.hoje.valor != null ? `${brl(previa.hoje.valor)} por mês` : '—'}
              </p>
              <p className="truncate text-[11px] text-muted-foreground" title={previa.hoje.resumo}>{previa.hoje.resumo}</p>
            </div>
            <div className="rounded-lg bg-[#F6ECEC] px-3 py-2 dark:bg-[#2A1F1F]">
              <p className="text-[10.5px] text-muted-foreground">{renovacao ? 'Renovação' : fibra ? 'Nos 3 primeiros meses' : 'Com a Claro Empresas'}</p>
              <p className="font-display text-[19px] font-semibold tabular-nums text-[#5B0A0D] dark:text-[#C87484]">
                {brl(renovacao ? semFibra : previa.total_3m)} por mês
              </p>
              <p className="truncate text-[11px] text-muted-foreground" title={previa.resumo_claro}>
                {renovacao ? resumoSemFibra(previa.itens) : previa.resumo_claro}
              </p>
            </div>
          </div>

          <div className="mt-2 border-t border-border">
            {agrupar(itensVisiveis).map((g) => {
              const nums = numerosDoGrupo(g.itens);
              const hojeLinha = hojeDoGrupo(g.itens);
              return (
                <div key={g.chave} className="flex items-start justify-between gap-3 border-b border-border px-3 py-1.5 text-[12px]">
                  <span className="min-w-0">
                    <span className="text-foreground">{rotuloDoGrupo(g.itens)}</span>
                    {g.itens[0].tipo === 'LINHA_NOVA' && bonus && (
                      <span className="ml-1.5 inline-flex items-center gap-1 align-middle">
                        <span className="rounded px-1.5 text-[11px] font-semibold text-[#5B0A0D] [background:#F6ECEC] dark:text-[#C87484] dark:[background:#2A1F1F]">{g.itens[0].gb} GB</span>
                        <span className="text-[11px] text-muted-foreground">+</span>
                        <span className="rounded px-1.5 text-[11px] font-semibold text-[#075E54] [background:#E3F1EE] dark:text-[#25D366] dark:[background:#1C2A26]">{bonus.gb} GB bônus</span>
                      </span>
                    )}
                    {hojeLinha && (
                      <span className="block truncate text-[10.5px] text-foreground" title={hojeLinha}>{hojeLinha}</span>
                    )}
                    {nums.length > 0 && (
                      <span className="block truncate text-[10.5px] text-muted-foreground" title={nums.join(' · ')}>{nums.join(' · ')}</span>
                    )}
                  </span>
                  <span className="flex-shrink-0 font-semibold tabular-nums text-[#5B0A0D] dark:text-[#C87484]">{valorDoGrupo(g.itens)}</span>
                </div>
              );
            })}
            {renovacao && fibra && (
              <div className="flex items-center justify-between gap-3 bg-[#FBF9F6] px-3 py-1.5 text-[12px] dark:bg-[#241A1A]">
                <span className="font-semibold">Com a fibra, nos 3 primeiros meses</span>
                <span className="font-semibold tabular-nums text-[#5B0A0D] dark:text-[#C87484]">{brl(previa.total_3m)} por mês</span>
              </div>
            )}
          </div>

          {previa.itens.some((i) => i.tipo === 'BLOCO') && (
            <p className="mx-3 mt-2 border-l-[3px] border-l-[#9D4459] bg-[#FBF4F4] px-2.5 py-1.5 text-[11px] text-[#5B0A0D] dark:bg-[#2A1F1F] dark:text-[#E8C6CD]">
              <b className="font-semibold">Bloco de Dados:</b> é um número a mais na conta, que funciona como uma linha só de internet.
              Os GB dele são divididos entre as linhas pelo Gestor de Dados. No pedido ele aparece como um número novo.
            </p>
          )}
        </>
      )}

      {previa.avisos.length > 0 && (
        <div className="mx-3 mt-2 space-y-1">
          {previa.avisos.map((a) => (
            <p key={a} className="rounded-md bg-[repeating-linear-gradient(135deg,#F3E6E8_0_6px,#FAF1F2_6px_12px)] px-2.5 py-1 text-[11px] text-[#5B0A0D] dark:bg-[repeating-linear-gradient(135deg,#2A1F1F_0_6px,#241A1A_6px_12px)] dark:text-[#C87484]">{a}</p>
          ))}
        </div>
      )}

      <div className="mt-2 flex flex-wrap items-center gap-2 border-t border-border px-3 py-1.5 text-[11px]">
        {ultimo ? (
          <>
            <span aria-hidden className="h-1.5 w-1.5 rounded-full bg-[#128C7E] dark:bg-[#25D366]" />
            <span className="font-semibold text-foreground">E-mail enviado {dataHora(ultimo.enviado_em)}</span>
            <span className="text-muted-foreground">
              · {ultimo.modelo || ultimo.campanha} · {ultimo.abriu ? 'abriu' : 'não abriu'} · {ultimo.clicou ? `clicou ${dataHora(ultimo.clicou_em)}` : 'não clicou'}
            </span>
            <button type="button" onClick={() => abrirEmail(ultimo.id)} className="ml-auto inline-flex items-center gap-1 font-semibold text-[#5B0A0D] hover:underline dark:text-[#C87484]">
              <Mail className="h-3 w-3" /> ver o e-mail
            </button>
          </>
        ) : (
          <>
            <span aria-hidden className="h-1.5 w-1.5 rounded-full border-[1.5px] border-[#8A7576]" />
            <span className="text-muted-foreground">Nenhum e-mail do TOP enviado a este cliente ainda.</span>
          </>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-2 px-3 pb-3 pt-1">
        <button
          type="button"
          disabled={previa.pedir_conta}
          onClick={() => onUsar(dados)}
          title={previa.pedir_conta ? 'Sem dado para montar a proposta: peça a conta ao cliente.' : 'Preenche o montador com esta proposta. Confira e salve.'}
          className="inline-flex h-[30px] items-center gap-1.5 rounded-md border-0 bg-[linear-gradient(90deg,#4A1320,#5B0A0D)] px-3.5 text-[12px] font-bold text-white transition-all hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-[linear-gradient(90deg,#9D2530,#6C2236)]"
        >
          <Wand2 className="h-3.5 w-3.5" /> Usar esta proposta
        </button>
        <span className="text-[10.5px] text-muted-foreground">preenche o montador abaixo: confira e salve</span>
      </div>

      <Dialog open={html !== null} onOpenChange={(o) => { if (!o) setHtml(null); }}>
        <DialogContent className="max-w-3xl">
          <DialogHeader><DialogTitle>E-mail que o cliente recebeu</DialogTitle></DialogHeader>
          <iframe title="E-mail enviado" sandbox="" srcDoc={html || ''} className="h-[70vh] w-full rounded-md border bg-white" />
        </DialogContent>
      </Dialog>
    </section>
  );
}
